import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Type, 
  Image as ImageIcon, 
  Code, 
  Quote, 
  List, 
  Grid3X3, 
  LayoutPanelLeft, 
  Film,
  Sparkles,
  Command,
  X,
  MousePointerClick
} from 'lucide-react';

// Block types definition - same as BlockEditor
const BLOCK_TYPES = [
  { type: 'heading', icon: Type, label: 'Heading', description: 'Large section heading', keywords: ['h1', 'h2', 'title', 'header'] },
  { type: 'paragraph', icon: Type, label: 'Paragraph', description: 'Plain text block', keywords: ['text', 'p', 'body'] },
  { type: 'image', icon: ImageIcon, label: 'Image', description: 'Upload or embed image', keywords: ['photo', 'picture', 'img', 'media'] },
  { type: 'button', icon: MousePointerClick, label: 'Button', description: 'Call-to-action button', keywords: ['cta', 'link', 'action', 'click', 'btn'] },
  { type: 'hero', icon: Film, label: 'Hero Banner', description: 'Full-width hero with text overlay', keywords: ['banner', 'header', 'cover', 'jumbotron'], category: 'featured' },
  { type: 'quote', icon: Quote, label: 'Quote', description: 'Blockquote for citations', keywords: ['blockquote', 'citation', 'testimonial'] },
  { type: 'list', icon: List, label: 'List', description: 'Bulleted list', keywords: ['bullet', 'ul', 'items'] },
  { type: 'code', icon: Code, label: 'Code', description: 'Code block with syntax highlighting', keywords: ['snippet', 'pre', 'programming'] },
  { type: 'columns', icon: Grid3X3, label: 'Columns (2-5)', description: 'Multi-column layout', keywords: ['grid', 'cols', 'layout', 'column'], category: 'layout' },
  { type: 'image-text-row', icon: LayoutPanelLeft, label: 'Image + Text (Row)', description: 'Side-by-side image and text', keywords: ['side', 'horizontal', 'split'], category: 'layout' },
  { type: 'image-text-stack', icon: LayoutPanelLeft, label: 'Image + Text (Stack)', description: 'Stacked image and text', keywords: ['vertical', 'stack', 'top'], category: 'layout' },
];

const SlashCommandMenu = ({ isOpen, onClose, onSelect, position, searchQuery = '' }) => {
  const [filteredBlocks, setFilteredBlocks] = useState(BLOCK_TYPES);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);

  // Filter blocks based on search query
  useEffect(() => {
    if (!searchQuery) {
      setFilteredBlocks(BLOCK_TYPES);
      setSelectedIndex(0);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = BLOCK_TYPES.filter(block => 
      block.label.toLowerCase().includes(query) ||
      block.description.toLowerCase().includes(query) ||
      block.keywords.some(keyword => keyword.includes(query))
    );
    setFilteredBlocks(filtered);
    setSelectedIndex(0);
  }, [searchQuery]);

  // Scroll selected item into view
  useEffect(() => {
    if (itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredBlocks.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredBlocks.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredBlocks[selectedIndex]) {
          onSelect(filteredBlocks[selectedIndex].type);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  }, [isOpen, filteredBlocks, selectedIndex, onSelect, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Group blocks by category
  const basicBlocks = filteredBlocks.filter(b => !b.category);
  const featuredBlocks = filteredBlocks.filter(b => b.category === 'featured');
  const layoutBlocks = filteredBlocks.filter(b => b.category === 'layout');

  let currentIndex = 0;
  const getItemIndex = () => currentIndex++;

  return (
    <div 
      ref={menuRef}
      className="fixed z-[55] bg-white rounded-xl shadow-2xl border border-slate-200 w-80 max-h-96 overflow-hidden"
      style={{ 
        top: position?.top || '50%',
        left: position?.left || '50%',
        transform: position ? 'none' : 'translate(-50%, -50%)'
      }}
      data-testid="slash-command-menu"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Command size={14} />
          <span className="font-medium">Add Block</span>
          {searchQuery && (
            <span className="text-indigo-600">"{searchQuery}"</span>
          )}
        </div>
        <button 
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="overflow-y-auto max-h-72">
        {filteredBlocks.length === 0 ? (
          <div className="px-4 py-8 text-center text-slate-500">
            <p className="text-sm">No blocks match "{searchQuery}"</p>
            <p className="text-xs mt-1">Try different keywords</p>
          </div>
        ) : (
          <div className="py-2">
            {/* Basic Blocks */}
            {basicBlocks.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Basic
                </div>
                {basicBlocks.map((block) => {
                  const itemIndex = getItemIndex();
                  const Icon = block.icon;
                  return (
                    <button
                      key={block.type}
                      ref={el => itemRefs.current[itemIndex] = el}
                      onClick={() => onSelect(block.type)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${
                        selectedIndex === itemIndex 
                          ? 'bg-indigo-50 text-indigo-700' 
                          : 'hover:bg-slate-50'
                      }`}
                      data-testid={`slash-add-${block.type}`}
                    >
                      <div className={`p-1.5 rounded-lg ${
                        selectedIndex === itemIndex ? 'bg-indigo-100' : 'bg-slate-100'
                      }`}>
                        <Icon size={16} className={
                          selectedIndex === itemIndex ? 'text-indigo-600' : 'text-slate-600'
                        } />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{block.label}</div>
                        <div className="text-xs text-slate-500 truncate">{block.description}</div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}

            {/* Featured Blocks */}
            {featuredBlocks.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-purple-500 uppercase tracking-wide flex items-center gap-1 mt-1 border-t border-slate-100 pt-2">
                  <Sparkles size={10} />
                  Featured
                </div>
                {featuredBlocks.map((block) => {
                  const itemIndex = getItemIndex();
                  const Icon = block.icon;
                  return (
                    <button
                      key={block.type}
                      ref={el => itemRefs.current[itemIndex] = el}
                      onClick={() => onSelect(block.type)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${
                        selectedIndex === itemIndex 
                          ? 'bg-purple-50 text-purple-700' 
                          : 'hover:bg-purple-50/50'
                      }`}
                      data-testid={`slash-add-${block.type}`}
                    >
                      <div className={`p-1.5 rounded-lg ${
                        selectedIndex === itemIndex ? 'bg-purple-100' : 'bg-purple-50'
                      }`}>
                        <Icon size={16} className="text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-purple-700">{block.label}</div>
                        <div className="text-xs text-purple-500/70 truncate">{block.description}</div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}

            {/* Layout Blocks */}
            {layoutBlocks.length > 0 && (
              <>
                <div className="px-4 py-1.5 text-xs font-semibold text-blue-500 uppercase tracking-wide mt-1 border-t border-slate-100 pt-2">
                  Layout
                </div>
                {layoutBlocks.map((block) => {
                  const itemIndex = getItemIndex();
                  const Icon = block.icon;
                  return (
                    <button
                      key={block.type}
                      ref={el => itemRefs.current[itemIndex] = el}
                      onClick={() => onSelect(block.type)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left ${
                        selectedIndex === itemIndex 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'hover:bg-blue-50/50'
                      }`}
                      data-testid={`slash-add-${block.type}`}
                    >
                      <div className={`p-1.5 rounded-lg ${
                        selectedIndex === itemIndex ? 'bg-blue-100' : 'bg-blue-50'
                      }`}>
                        <Icon size={16} className="text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-blue-700">{block.label}</div>
                        <div className="text-xs text-blue-500/70 truncate">{block.description}</div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">↑↓</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">↵</kbd>
            select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">esc</kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
};

export default SlashCommandMenu;
export { BLOCK_TYPES };
